import { api } from "@/services/api"
import { MAX_UPNS_PER_REQUEST, chunk, summarizeAssignments } from "./lib/teamMembership"
import type { MembershipChangeSummary } from "./lib/teamMembership"
import type { BookableEventTypeSlug } from "./lib/slotTypes"

// ═══════════════════════════════════════════════════════════════════
//  THE STAFF CALENDAR'S API
//
//  Everything here mirrors an endpoint that exists. Sites are addressed
//  the way the server addresses them — by Cal team, identified by slug —
//  because every site publishes the same event type slugs, so a slug pair
//  is what CMS stores and sends (see CalEventTypeSlugs.cs).
// ═══════════════════════════════════════════════════════════════════

export interface HostSummary {
  id: number
  name?: string
}

export interface AttendeeSummary {
  name: string
  email?: string
  phoneNumber?: string
}

/** Matches AmsBookingsController's GetBookings/GetBooking response (BookingSummary). */
export interface BookingDetails {
  uid: string
  status: string
  start: string
  end: string
  eventTypeId: number
  /** Cal's free-text location, which is whatever the event type was configured with. */
  location?: string
  /** The MSC office, resolved server-side from the event type's team. Null on a team no site claims. */
  siteName?: string
  hosts: HostSummary[]
  attendees: AttendeeSummary[]
  /**
   * The booking's own facts as CMS writes them: the reference code, who booked it, and the
   * confirmation code — which the server includes on every staff read (anyone with
   * appointments:view, AB#766). It is absent only when the booking has no stored code, e.g. a
   * booking made outside CMS.
   *
   * What was written onto the booking in Cal when it was made — now the confirmation code, who
   * booked it, and anything else the caller explicitly sent. An open set of keys, so it is rendered
   * as it arrives rather than destructured. Absent on a booking made outside CMS.
   *
   * It no longer carries the household questionnaire. Those answers are columns on the CMS booking
   * request and arrive as `BookingFormData` below, because Cal serializes a booking's metadata into
   * the attendee's own confirmation email — see PublicBookingOperations.BuildMetadata.
   */
  metadata?: Record<string, string>
  /**
   * How many notes staff have written on this booking (both visibilities, never CMS's own system
   * notes), for the cell's "Has notes" marker. A count only: the text is read from the notes
   * endpoint. Absent from a server that predates it.
   */
  noteCount?: number
  /**
   * The CMS booking request, where the server joined one (the override detail's affected bookings,
   * AB#811): what the manual reschedule workspace pairs. Absent elsewhere.
   */
  requestId?: string | null
  /** The site's Cal team slug, when the server sent it. */
  teamSlug?: string | null
  /** The slot type's event type slug ("intake", "off-slot", "private"), when the server sent it. */
  eventTypeSlug?: string | null
  /**
   * Staff recorded this appointment after its time had passed, with no Cal booking behind it and no
   * message to the household (AB#868). Its `uid` is then the synthetic `cms:<request id>`, which the
   * dialog's reads accept and every Cal-backed write refuses (409 `notInSchedulingSystem`), so those
   * actions are hidden for it. Absent (false) on every other booking.
   */
  recordedWithoutScheduling?: boolean
}

/**
 * What the applicant actually filled in, for one appointment. Matches AmsBookingsController's
 * `POST /api/ams/bookings/{uid}/request` response (AmsBookingFormData).
 *
 * Read from CMS rather than from Cal, which is the point of it: this is the system of record for the
 * booking form, and every field is optional because the two channels ask for different amounts. A
 * staff booking taken at a counter has a name and a phone number and often nothing else; a public
 * booking has been through the whole questionnaire.
 */
export interface BookingFormData {
  /** The CMS booking request, which is what addresses the review queue and the SSN audit trail. */
  requestId: string
  /** Safe to show and safe to accept back — unlike the six-digit confirmation code. */
  referenceCode: string
  /** AmsBookingRequestStatus, as a number — see `lib/bookingStatus`. */
  status: number
  firstName?: string
  lastName?: string
  phoneNumber?: string
  email?: string
  /** Whether they agreed, on this booking, to be texted about it. */
  smsOptIn: boolean
  /** ISO yyyy-MM-dd. */
  dateOfBirth?: string
  /**
   * Nine digits in the clear, or absent when none was given or the retention sweep has erased it.
   *
   * The server decrypts this out of a vault row and writes an audit row naming whoever read it, so
   * every response that carries it is a recorded disclosure. Do not persist it, do not put it in a
   * query string, and do not log it: this object is the only place it should exist in the browser,
   * and only for as long as the dialog is open.
   */
  ssn?: string
  address?: string
  /** The ZIP typed at step 1, which is what the slot was reserved against. Absent on a staff booking. */
  zipCode?: string
  householdSize?: number
  /** Monthly, not annual — the figure the form asks for. */
  householdIncome?: number
  energySource?: string
  pseService?: string
  /** The utility account number, as typed. Unvalidated; providers number accounts differently. */
  energyAccountNumber?: string
  /** The staff member who booked it, or absent for an appointment the applicant made themselves. */
  createdByUpn?: string
  /**
   * The gated programs the household screening left open, by AmsBookingPrograms name ("Pse",
   * "Liheap"). Null or absent means the booking was never checked — not the same as an empty list,
   * which means checked and eligible for none. See `describeScreening`.
   */
  eligiblePrograms?: string[] | null
  /** The programs the household appears already to have been served for this year. Null when not checked. */
  blockedPrograms?: string[] | null
  /** The CMS person the screening matched, if any. A link for staff holding `people:view`. */
  matchedPersonId?: string | null
}

export type BookingStatusFilter = "upcoming" | "recurring" | "past" | "cancelled" | "unconfirmed"

/**
 * Where one booking stands in CMS: what happened at it, and what may happen to it next. Matches
 * AmsBookingRequestsController's `GET /api/ams/booking-requests/{id}` (AmsBookingStatusView).
 *
 * Separate from `BookingFormData` on purpose: every read of that one is an audited disclosure of a
 * Social Security number, and this one is refetched after every status change.
 */
export interface BookingStatusView {
  requestId: string
  calBookingUid?: string | null
  /** AmsBookingRequestStatus — see `lib/bookingStatus`. */
  status: number
  /** Why it is in this status: the reschedule reason, the note on Other, or a correction's explanation. */
  statusReason?: string | null
  /**
   * The booking's Cal event type slug (`intake`, `private` or `off-slot`) — what a change of type
   * (AB#767) leaves out of the types it offers. Absent from a server that predates it.
   */
  eventTypeSlug?: string | null
  statusChangedAt?: string | null
  statusChangedBy?: string | null
  cancelledAt?: string | null
  /** AmsBookingActor. */
  cancelledBy?: number | null
  cancellationReason?: string | null
  slotStart?: string | null
  slotEnd?: string | null
  hostCalUserId?: number | null
  hostName?: string | null
  rescheduledToRequestId?: string | null
  rescheduledToCalBookingUid?: string | null
  rescheduledFromRequestId?: string | null
  rescheduledFromCalBookingUid?: string | null
  /**
   * The statuses the signed-in person may move this booking to now. Decided by the server, which
   * knows whether they host it and administer appointments — empty means "read only for you".
   */
  allowedStatuses: number[]
  /** The staff member who cancelled, as their UPN. Null unless a staff member did. */
  cancelledByUpn?: string | null
  /** Who linked this booking by hand to the one that replaced it. */
  rescheduleLinkedBy?: string | null
  rescheduleLinkedAt?: string | null
  /** The earliest moment an outcome may be recorded: the slot start. */
  outcomeOpensAt?: string | null
  /** Whether the signed-in person could change this booking's status at all (administrator, or its host). */
  canUpdate?: boolean
  /**
   * An administrator booked this on top of another appointment on the same host, on purpose. The
   * one exception to "one booking per host at a time"; who, when and why are below.
   */
  doubleBookingOverride?: boolean
  /** Who made the double-booking override, as their UPN. */
  doubleBookingOverrideBy?: string | null
  doubleBookingOverrideAt?: string | null
  /** Why. Required whenever the override is set. */
  doubleBookingOverrideReason?: string | null
  /**
   * A manager booked this past the host's daily limit, on purpose (AB#606). Who, when and why are
   * below. Staff-only, like the double-booking override.
   */
  dailyLimitOverride?: boolean
  /** Who made the daily-limit override, as their UPN. */
  dailyLimitOverrideBy?: string | null
  dailyLimitOverrideAt?: string | null
  /** Why. Required whenever the override is set. */
  dailyLimitOverrideReason?: string | null
}

/** The staff cancel's body (StaffCancelBookingRequest). */
export interface StaffCancelBookingArgs {
  bookingUid: string
  /** Staff-only. Required by the server, 3–500 characters; what Cal is given as the reason. */
  internalNote?: string
  /** Meant for the household. Optional, at most 500 characters. */
  externalNote?: string
  /** @deprecated The old name for `internalNote`. */
  note?: string
}

/** AmsBookingNoteVisibility. */
export const BookingNoteVisibility = { Internal: 0, External: 1 } as const

/** AmsBookingNoteAction. */
export const BookingNoteAction = {
  General: 0,
  Cancellation: 1,
  Reschedule: 2,
  StatusChange: 3,
  ReviewDecision: 4,
  RescheduleLink: 5,
  System: 6,
} as const

/** One note on a booking (AmsBookingNoteItem). */
export interface BookingNote {
  id: string
  /** AmsBookingNoteVisibility — see `BookingNoteVisibility`. */
  visibility: number
  /** AmsBookingNoteAction — see `BookingNoteAction`. */
  action: number
  body: string
  createdBy: string
  createdAt: string
}

/** One entry in a booking's status history (AmsBookingStatusChangeItem). */
export interface BookingStatusChange {
  fromStatus?: number | null
  toStatus: number
  reason?: string | null
  changedBy: string
  /** AmsBookingStatusChangeSource: 0 staff, 1 attendee, 2 system, 3 Cal. */
  source: number
  changedAt: string
}

/** A booking request in a list (AmsBookingRequestListItem). */
export interface BookingRequestListItem {
  id: string
  calBookingUid?: string | null
  referenceCode: string
  status: number
  slotStart?: string | null
  slotEnd?: string | null
  siteName?: string | null
  calTeamSlug: string
  attendeeName?: string | null
  hostCalUserId?: number | null
  hostName?: string | null
  statusChangedAt?: string | null
  statusChangedBy?: string | null
  /**
   * The uid the appointment dialog opens it by (AB#868): the Cal uid, or the synthetic `cms:` uid of
   * an appointment recorded without scheduling. Null when there is nothing to open; absent from a
   * server that predates it, when `calBookingUid` is the fallback — see `openableUid`.
   */
  openUid?: string | null
}

/** The incomplete-bookings worklist (NeedsUpdateResponse). */
export interface NeedsUpdateResponse {
  items: BookingRequestListItem[]
  total: number
  /** Whether this is everyone's list or the caller's own. */
  isAdmin: boolean
  /** Staff with no Cal account host nothing, so their list is empty for that reason. */
  noCalAccount: boolean
}

/**
 * A date override on one of the schedules governing a host — the thing that made them unavailable.
 *
 * `scope` is what lets the grid tell a closure from an absence: `event-type` sits on the site's own
 * schedule and applies to everybody there, `host` sits on one person's and applies to them alone.
 */
export interface AvailabilityOverride {
  scope: "event-type" | "host"
  /** The calendar day it names, "YYYY-MM-DD". */
  date: string
  /** Wall-clock "HH:mm" in the schedule's zone. Equal to `endTime` when the day is cancelled outright. */
  startTime: string
  endTime: string
  /** Free text set by whoever administers the schedule — "Snow closure", "Out sick". */
  reason?: string
}

/**
 * One host's availability at a site. Matches AmsAvailabilityController's GetAvailability response
 * (CalHostAvailability).
 *
 * These are Cal's own computed answer — the site's hours narrowed by the person's own schedule,
 * with date overrides applied — not inputs to re-derive. Cal refuses to publish a slot outside
 * `dateRanges`, which is exactly why the calendar can trust it enough to draw a row as unavailable.
 */
export interface HostAvailability {
  /** The Cal account — the same id a booking's host carries, which is how it finds its column. */
  userId: number
  /**
   * When this host is bookable. Anything else in the window asked about is not.
   *
   * `eventTypeId` is which of the site's event types published the range. The server reads every one
   * of them and concatenates the answers — a site's hours are split across them, intake taking
   * Monday to Thursday and off-slot taking Friday — so without it a free row says when somebody is
   * bookable but not what for.
   */
  dateRanges: { start: string; end: string; eventTypeId: number }[]
  /** The overrides that took the rest away, so a row can be labelled rather than merely shaded. */
  overrides: AvailabilityOverride[]
  /**
   * The days ("YYYY-MM-DD", Pacific) in the window on which this host has already reached their daily
   * limit of appointments at the site (AB#606) — CMS's own count, not Cal's: Cal applies the cap by
   * withholding the rest of the day, so a capped day is missing from `dateRanges` exactly as a day off
   * is. The grid labels these "Daily limit reached". Optional so an older server's answer still reads.
   */
  cappedDays?: string[]
}

// ── Teams ─────────────────────────────────────────────────────────────
// Cal owns team membership; CMS administers it through AmsTeamsController. A "team" here is one MSC
// site that takes appointments.

/**
 * The hours a site's calendar grid draws, from server configuration (`AmsGridWindowOptions`,
 * AB#783): wall-clock "HH:mm" in the team's zone, `end` exclusive — "16:30" for a day whose last
 * row starts at 4:00 PM.
 */
export interface GridWindow {
  start: string
  end: string
}

/** Matches AmsTeamsController's GetTeams response (CalTeam). */
export interface CalTeam {
  id: number
  name: string
  /** The stable identity a booking query filters on. Null for a team Cal has not slugged. */
  slug?: string
  bio?: string
  timeZone?: string
  /** A main office rather than an outreach site — the server's `ServiceAreaService.IsMainOffice`. */
  isMainOffice: boolean
  /** The window the grid draws for this team. Absent from an older API; the grid then uses its own default. */
  gridWindow?: GridWindow | null
}

/** Matches AmsTeamsController's GetTeamMembers response (CalTeamMember). Carries no name — join to cal-users for that. */
export interface CalTeamMember {
  userId: number
  teamId: number
  role: string
  accepted: boolean
}

/** Matches AmsCalUsersController's GetUsers response (CalOrganizationUser). */
export interface CalOrganizationUser {
  id: number
  email: string
  username?: string
  name?: string
  timeZone?: string
}

// ── Membership, in CMS's own identifiers ──────────────────────────────
// Team membership is administered through api/ams/team-assignments rather than the Cal-id endpoints:
// it names a team by slug and a person by principal name, and creates the Cal account for someone
// who does not have one yet. See AmsTeamAssignmentsController.

/** Matches UsersController's GetAllUsers response (UserDto) — CMS's own staff, not Cal accounts. */
export interface CmsUser {
  id: string
  displayName: string
  /** Their sign-in address. Also the identity a Cal account is matched on, by email. */
  userPrincipalName: string
  /** False for someone deactivated. They may not be put on a team — the server refuses it. */
  isActive: boolean
}

/** One (team, person) pair's outcome. Matches TeamAssignmentResult. */
export interface TeamAssignmentResult {
  teamSlug: string
  teamId: number
  upn: string
  calUserId?: number
  /**
   * The status enum, which the API serializes as its ordinal. Callers should read `error` instead:
   * it is set on exactly the failures and survives any renumbering of the enum.
   */
  status: number
  /** Why Cal refused this pair. Absent on every non-failure. */
  error?: string
}

/**
 * The outcome of a bulk assignment. Matches TeamAssignmentReport.
 *
 * A request naming an unknown slug, an unknown person or a deactivated one performs no writes at all
 * and comes back as a 400/404 instead of this.
 */
export interface TeamAssignmentReport {
  results: TeamAssignmentResult[]
  unknownTeamSlugs: string[]
  unknownUpns: string[]
  inactiveUpns: string[]
}

/**
 * Matches AmsTeamsController's GetTeamEventTypes response (CalTeamEventType).
 *
 * `lengthInMinutes` is where the grid's slot cadence comes from: it is how long an appointment at
 * this site actually takes, configured in Cal rather than guessed here.
 */
export interface CalTeamEventType {
  id: number
  teamId: number
  title: string
  slug: string
  lengthInMinutes: number
  schedulingType?: string
  scheduleId?: number
  hostUserIds: number[]
}

// ── One staff member's schedule ───────────────────────────────────────
// The personal week, in two shapes. `getMySchedule` takes whose week it is from the token and can
// address nobody else, which is what makes it reachable with appointments:view alone; an
// administrator opening a colleague's week uses `getStaffSchedule`, behind appointments:manage. The
// payload is the same either way — see AmsMyScheduleController and AmsStaffScheduleController.

/**
 * How far the server could resolve the caller. Matches StaffScheduleStatus, serialized camelCase.
 *
 * Three outcomes rather than an empty grid for all three: someone Cal has no account for, and
 * someone assigned to no site, are half-onboarded rather than unbooked, and only a page that can
 * tell them apart can say so.
 */
export type StaffScheduleStatus = "ok" | "noCalAccount" | "noTeam"

/**
 * One site the caller hosts at, with its own week attached. Matches StaffScheduleSite.
 *
 * Per site rather than one merged week, because the two things that make a week readable are both
 * properties of a site: the hours somebody works at this office are not the hours they work at the
 * other, and an availability read that fails fails for one site at a time.
 */
export interface StaffScheduleSite {
  teamId: number
  /** Null for a team Cal never slugged; it can take no bookings, so it contributes none. */
  teamSlug?: string
  teamName: string
  timeZone?: string
  /** A main office rather than an outreach site — the server's `ServiceAreaService.IsMainOffice`. */
  isMainOffice: boolean
  /** The window the week draws for this site. Absent from an older API; the grid then uses its own default. */
  gridWindow?: GridWindow | null
  eventTypeId: number
  eventTypeSlug: string
  /** How long an appointment here takes — the row height of this site's part of the week. */
  lengthInMinutes: number
  /** Appointments here the caller hosts. The narrowing to them is the server's, not this client's. */
  bookings: BookingDetails[]
  /**
   * The site's off-slot event types this person hosts, by Cal id — matched against a date range's
   * `eventTypeId` to tell a free off-slot row from a free intake one. Empty when they host none here.
   */
  offSlotEventTypeIds: number[]
  /**
   * The site's private event types this person hosts (AB#779), matched the same way to tell a free
   * private row from a public one. Optional: an API from before private existed does not send it.
   */
  privateEventTypeIds?: number[]
  /** Their bookable time here, as Cal computes it. Empty when `availabilityUnavailable`. */
  availability: HostAvailability[]
  /**
   * This site's availability read failed, so the week knows what is booked here but not when they
   * were working. The grid must draw no shading at all for it — shading it from the other sites'
   * hours would report the time they actually work here as time off.
   */
  availabilityUnavailable: boolean
}

/** Matches AmsMyScheduleController's GetMySchedule response (StaffSchedule). */
export interface StaffSchedule {
  status: StaffScheduleStatus
  /** The caller's Cal account. Absent unless `status` is "ok". */
  calUserId?: number
  sites: StaffScheduleSite[]
  /** Appointments on an event type none of their sites claims. Vanishingly rare, counted not dropped. */
  otherBookings: BookingDetails[]
}

/** What a cancellation returns. Matches CancelBookingResult. */
export interface CancelBookingResult {
  /** When the server cancelled it. */
  cancelledAt: string
}

/** Matches AmsBookingsController's StaffCreateBookingAttendee. Name and phone are the only requirements. */
export interface StaffCreateBookingAttendee {
  firstName: string
  lastName: string
  /** E.164, as `react-phone-number-input` produces it. */
  phoneNumber: string
  email?: string
  timeZone?: string
  /** ISO yyyy-MM-dd. */
  dateOfBirth?: string
  /** Nine digits. Sent once; the server tokenizes and encrypts it and it never comes back. */
  ssn?: string
  /** Whether they agreed, on this booking, to be texted about it. Never pre-ticked. */
  smsOptIn?: boolean
  /** The utility account number, as typed. Unvalidated; providers number accounts differently. */
  energyAccountNumber?: string
}

/** Matches AmsBookingsController's StaffCreateBookingHousehold. Every answer optional. */
export interface StaffCreateBookingHousehold {
  householdSize?: number
  /** Monthly, not annual — the same figure the public flow asks for. */
  householdIncome?: number
  address?: string
  energySource?: string
  pseService?: string
}

/** Matches AmsBookingsController's StaffCreateBookingRequest. */
export interface StaffCreateBookingRequest {
  teamSlug: string
  /** One per slot type — see `lib/slotTypes`. Off-slot needs `appointments:manage`; the others `appointments:book`. */
  eventTypeSlug: BookableEventTypeSlug
  /** ISO instant for the slot's start. */
  start: string
  /** Cal's id for the host whose column was clicked, not a CMS user id. */
  hostCalUserId: number
  /** Stable across retries of one form; see the mutation's remarks. */
  bookingKey: string
  attendee: StaffCreateBookingAttendee
  household?: StaffCreateBookingHousehold
  /** Books despite an upcoming appointment on the same SSN. Sent only on a second, deliberate submit. */
  overrideDuplicate?: boolean
  /**
   * Books the host although they already have an appointment then — an administrator's deliberate
   * double booking (`appointments:manage`). Sent only after a 409 `hostAlreadyBooked`, with a reason.
   */
  overrideDoubleBooking?: boolean
  /** Why. Required with `overrideDoubleBooking` or `overrideDailyLimit`; at most 500 characters. One reason serves both. */
  overrideReason?: string
  /**
   * Books the host although they have already reached their daily limit that day — a manager's
   * deliberate decision (`appointments:manage`, AB#606). Sent only after a 409 `dailyLimitReached`,
   * with a reason.
   */
  overrideDailyLimit?: boolean
  /**
   * Records the appointment with no message to the household (AB#868): a time already past is
   * recorded in CMS alone, with no Cal booking; a time inside the 8-hour notice is booked in Cal
   * with the notice check skipped. Required with a reason of 1-500 characters; a time further ahead
   * is refused 400 `manualRecordNotApplicable`.
   */
  manualRecordReason?: string
}

/** Matches AmsBookingsController's StaffCreateBookingResult. */
export interface StaffCreateBookingResult {
  uid: string
  start: string
  confirmationCode: string
  /** Who Cal gave it to; null when that could not be read back. */
  assignedHostCalUserId?: number
  /**
   * Whether that is the host who was asked for. False means the appointment exists but sits on
   * somebody else's day — Cal treats a host constraint as a preference and falls back to the rest of
   * the round-robin pool. Worth telling the user rather than closing quietly.
   */
  hostAsRequested: boolean
  /**
   * The appointment was recorded without scheduling (AB#868): `uid` is the synthetic `cms:` one and
   * no Cal booking exists. Absent otherwise.
   */
  recordedWithoutScheduling?: boolean
}

/**
 * How far a host reassignment got. Matches BookingReassignmentResultKind.
 *
 * Only `Reassigned` comes back as a 2xx. The rest arrive as the body of a 409 or a 502, which is why
 * the UI reads this rather than relying on the status code alone: "the host is busy" and "the
 * scheduling system would not do it" need different things said to the user, and both are conflicts.
 */
export type BookingReassignmentResultKind =
  | "Reassigned"
  | "AlreadyOnTargetHost"
  // Refused unless the working-hours override is sent (409, code "hostOutsideWorkingHours", AB#822).
  // Under a double booking it is the shift asked again: the move needs both overrides together.
  | "HostOutsideWorkingHours"
  // Refused unless an administrator sends the double-booking override (409, code "hostAlreadyBooked").
  | "HostAlreadyBooked"
  | "FallbackHostRejected"
  | "ReplacementFailed"
  | "OrphanedDuplicate"
  | "OrphanedCancellation"
  | "CancellationUnconfirmed"
  // Another booking took the host's slot after the check; CMS undid the replacement. The original was
  // already cancelled, so the household has no appointment (409, code "slotUnavailable").
  | "ReplacementHostClash"
  // The target is at their daily limit that day (AB#606); nothing was done. Refused unless a manager
  // sends the daily-limit override (409, code "dailyLimitReached").
  | "DailyLimitReached"

/**
 * What a reassignment did. Matches BookingReassignmentOutcome.
 *
 * `newBookingUid` differs from `previousBookingUid` on success: there is no reassign operation in
 * Cal, so the appointment is recreated on the new host and the original cancelled, which issues a
 * new uid. Anything holding the old one needs to refetch.
 */
export interface BookingReassignmentOutcome {
  kind: BookingReassignmentResultKind
  reassignmentId?: string | null
  previousBookingUid?: string | null
  newBookingUid?: string | null
  assignedCalUserId?: number | null
  /** The household's existing code, carried across unchanged so what they wrote down still works. */
  confirmationCode?: string | null
  detail?: string | null
}

// Membership and cancellation are the things here that are written as well as read, so they are the
// things that need cache tags: without them the page would keep showing the team as it was before
// the edit, and the grid would keep drawing a cancelled appointment, until something else happened
// to refetch them.
// AmsBookingNotes is notesApi's tag; registered here too so the writes that leave a note behind (a
// status change, a reschedule link) can invalidate the notes list themselves.
// AmsCalUsers is written as a side effect: putting someone on a team creates their Cal account if
// they had none, and until the organization's users are read again the new column has no name.
const taggedApi = api.enhanceEndpoints({ addTagTypes: ["AmsTeamMembers", "AmsCalUsers", "AmsBookings", "AmsBookingStatus", "AmsBookingNotes"] })

// How long a calendar read stays in the store once nothing on screen is using it: the whole session.
//
// RTK Query's default is sixty seconds, which made stepping back to a day or a colleague's week a
// minute later a fresh load — a grid already seen, drawn again from nothing. Staff move between days
// and people far more often than the schedule changes under them, and the toolbar's refresh is the
// way back to Cal's truth for the one in front of them. This app's own writes still reach every
// cached day without it: they invalidate `AmsBookings`, and an entry nothing is subscribed to is
// dropped on invalidation rather than kept, so it is read afresh when next opened.
const KEEP_FOR_SESSION = Infinity

export const bookingCalendarApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    getTeams: builder.query<CalTeam[], void>({
      query: () => ({ url: "/api/ams/teams" }),
      keepUnusedDataFor: KEEP_FOR_SESSION,
    }),
    getTeamMembers: builder.query<CalTeamMember[], { teamId: number }>({
      query: ({ teamId }) => ({ url: `/api/ams/teams/${teamId}/members` }),
      keepUnusedDataFor: KEEP_FOR_SESSION,
      providesTags: (_result, _error, { teamId }) => [{ type: "AmsTeamMembers" as const, id: teamId }],
    }),
    /** CMS's own staff — the people a membership names, before Cal knows anything about them. */
    getCmsUsers: builder.query<CmsUser[], void>({
      query: () => ({ url: "/api/users" }),
    }),
    /**
     * Applies a whole membership edit: everyone in `add` onto the team, everyone in `remove` off it.
     *
     * Two endpoints, so two calls — a single mutation because it is a single act to the user, and a
     * half-applied edit should surface as one outcome rather than two spinners. Additions go first:
     * if a removal is what Cal refuses (it will not let the last owner go), the people who were
     * meant to join have joined anyway.
     */
    setTeamMembership: builder.mutation<MembershipChangeSummary, {
      /** The team, by slug. A team Cal never slugged cannot be addressed and must not be offered. */
      teamSlug: string
      add: string[]
      remove: string[]
    }>({
      async queryFn({ teamSlug, add, remove }, _queryApi, _extraOptions, baseQuery) {
        // Batched because the endpoint caps one request's principal names, and a site with a long
        // roster would otherwise fail validation rather than doing the obvious thing.
        async function post(action: "add" | "remove", upns: string[]) {
          const reports: TeamAssignmentReport[] = []

          for (const batch of chunk(upns, MAX_UPNS_PER_REQUEST)) {
            const result = await baseQuery({
              url: `/api/ams/team-assignments/${action}`,
              method: "POST",
              body: { teamSlugs: [teamSlug], upns: batch },
            })

            if (result.error) return { error: result.error }

            reports.push(result.data as TeamAssignmentReport)
          }

          return { reports }
        }

        const added = await post("add", add)
        if (added.error) return { error: added.error }

        const removed = await post("remove", remove)
        if (removed.error) return { error: removed.error }

        return { data: summarizeAssignments(added.reports!, removed.reports!) }
      },
      // AmsCalUsers too: an addition can create the Cal account the new member's name comes from.
      invalidatesTags: ["AmsTeamMembers", "AmsCalUsers"],
    }),
    getTeamEventTypes: builder.query<CalTeamEventType[], { teamId: number }>({
      query: ({ teamId }) => ({ url: `/api/ams/teams/${teamId}/event-types` }),
      keepUnusedDataFor: KEEP_FOR_SESSION,
    }),
    /** Every user in the MSC Cal organization — the only place a member's display name comes from. */
    getCalUsers: builder.query<CalOrganizationUser[], void>({
      query: () => ({ url: "/api/ams/cal-users" }),
      keepUnusedDataFor: KEEP_FOR_SESSION,
      providesTags: ["AmsCalUsers"],
    }),
    /**
     * Bookings at the named teams. `start`/`end` bracket the window server-side; without them Cal
     * is asked for every booking the site has ever taken.
     *
     * `status` is normally left off, which asks for every status inside that window. Cal cuts its
     * status buckets against now rather than against the window — "upcoming" is "ends in the
     * future" — so naming one loses the earlier half of any span containing the present moment.
     * The server refuses a statusless read that has no window, for the reason above.
     */
    getBookings: builder.query<BookingDetails[], {
      teamSlugs: string[]
      eventTypeSlugs?: string[]
      status?: BookingStatusFilter
      start?: string
      end?: string
    }>({
      query: ({ teamSlugs, eventTypeSlugs, ...rest }) => ({
        url: "/api/ams/bookings",
        params: {
          teamSlugs: teamSlugs.join(","),
          ...(eventTypeSlugs?.length ? { eventTypeSlugs: eventTypeSlugs.join(",") } : {}),
          ...rest,
        },
      }),
      keepUnusedDataFor: KEEP_FOR_SESSION,
      providesTags: ["AmsBookings"],
    }),
    /**
     * What the applicant filled in for one appointment, from CMS rather than from Cal.
     *
     * A query despite being a POST, and the POST is not a mistake: the response carries a Social
     * Security number in the clear, and a GET would put this URL in the browser's history and in
     * every proxy log between here and the operator, and would be eligible for the API's output
     * cache. The endpoint's own remarks set this out; `AmsBookingReviewController.RevealSsn` is POST
     * for the same reason.
     *
     * For that same reason the result is deliberately **not** cached across dialogs: `keepUnusedData-
     * For` is zero, so closing the dialog drops the number out of the store rather than leaving it
     * sitting in memory behind whatever the operator opens next. The cost is one request per open,
     * which is the right trade for this payload — and every open that carries the number is a real
     * disclosure that the server records as one. Every caller with `appointments:view` gets it (AB#845
     * removed the separate reveal permission); a form that never held one has `ssn` absent.
     *
     * A 404 is an ordinary answer, not an error: an appointment made directly in Cal, or one
     * predating the booking-request table, has no form behind it. Callers render the dialog without
     * this section rather than showing a failure.
     */
    getBookingFormData: builder.query<BookingFormData, string>({
      query: (bookingUid) => ({
        url: `/api/ams/bookings/${encodeURIComponent(bookingUid)}/request`,
        method: "POST",
      }),
      keepUnusedDataFor: 0,
    }),
    /**
     * Cancels one appointment, as staff, by its Cal uid.
     *
     * The staff counterpart to the public phone + confirmation-code cancel: this one is addressed by
     * uid, because the calendar already has it, and needs `appointments:manage`.
     *
     * `internalNote` is required and becomes the booking's cancellation reason in Cal. `externalNote`
     * is optional text meant for the household. `note` is the old name for `internalNote`, still
     * accepted by the server.
     *
     * Named for the staff half deliberately: endpoint names are global to the root api, and
     * `cancelBooking` is already the public flow's phone + code cancel (publicBookingFlowApi). Two
     * injections of one name is not an error — the later one is dropped and its hook never exists.
     */
    cancelBookingAsStaff: builder.mutation<CancelBookingResult, StaffCancelBookingArgs>({
      query: ({ bookingUid, internalNote, externalNote, note }) => ({
        url: `/api/ams/bookings/${encodeURIComponent(bookingUid)}/cancel`,
        method: "POST",
        body: { internalNote: internalNote ?? note, externalNote },
      }),
      // Both grids draw bookings, and neither can tell which of them was showing the cancelled one.
      invalidatesTags: ["AmsBookings", "AmsBookingStatus"],
    }),
    /**
     * Moves an appointment to a different host.
     *
     * `targetCalUserId` is Cal's id, not a CMS user id — CMS stores no mapping between the two, so
     * the caller has to supply Cal's. The staff schedule already carries it as
     * `StaffScheduleSite.hosts[].calUserId`, which is where the host picker gets it.
     *
     * `reason` reaches the household: it is sent to Cal as the original booking's cancellation
     * reason, and Cal puts that in the cancellation email. Same constraint as the cancel note.
     *
     * `override` books the host even when they are off shift — the manager override for covering an
     * absence. It is a real one: the scheduling system is told to skip its own availability check
     * too, so the appointment lands on the host that was chosen rather than being quietly handed to
     * whoever was free. It does not reach a host who is already booked then; that is refused either
     * way, before anything is cancelled.
     *
     * `overrideDoubleBooking` (with `overrideReason`) is the other override, and the only one that
     * reaches a host who is already booked: an administrator's deliberate double booking. Sent only
     * after the server has answered 409 `hostAlreadyBooked`, or the picker already knew the host was
     * booked then.
     *
     * `overrideDailyLimit` (sharing `overrideReason`) moves it onto a host who has already reached
     * their daily limit that day (AB#606). Sent only after the server has answered 409
     * `dailyLimitReached`; the working-hours `override` does not get past the cap.
     *
     * Invalidates on every outcome, success or not. The move cancels the original before creating
     * its replacement, so even a failure can have changed what Cal holds, and the grid is the only
     * thing the user has to check with.
     */
    reassignBookingAsStaff: builder.mutation<
      BookingReassignmentOutcome,
      {
        bookingUid: string
        targetCalUserId: number
        reason?: string
        override?: boolean
        overrideDoubleBooking?: boolean
        overrideReason?: string
        overrideDailyLimit?: boolean
      }
    >({
      query: ({ bookingUid, targetCalUserId, reason, override, overrideDoubleBooking, overrideReason, overrideDailyLimit }) => ({
        url: `/api/ams/bookings/${encodeURIComponent(bookingUid)}/reassign`,
        method: "POST",
        body: { targetCalUserId, reason, override, overrideDoubleBooking, overrideReason, overrideDailyLimit },
      }),
      invalidatesTags: ["AmsBookings", "AmsBookingStatus"],
    }),
    /**
     * Books an appointment onto an open slot, as staff, on somebody else's behalf.
     *
     * The staff counterpart to the public booking flow, and the shapes differ in the one thing that
     * matters: the public flow books whatever slot its session is *holding*, and the hold decides the
     * site, the event type and the time. There is no hold here — staff name all three, plus the host —
     * so it needs `appointments:manage` rather than a reservation.
     *
     * Only `firstName`, `lastName` and `phoneNumber` are required. The household block the public
     * flow insists on is optional: staff book from a counter where those answers do not exist yet.
     *
     * `bookingKey` must be stable across retries of one submission and different between forms. It is
     * what makes a double-clicked submit produce one appointment rather than two, and it tags the
     * placeholder attendee address when no email is given — two email-less bookings sharing one
     * address make Cal answer the second with the first. Once the server has definitively refused a
     * submission (any 4xx), the next one is a new attempt and needs a new key.
     *
     * Named for the staff half for the same reason as `cancelBookingAsStaff` above: `createBooking`
     * is already the public flow's, and endpoint names are global to the root api.
     */
    createBookingAsStaff: builder.mutation<StaffCreateBookingResult, StaffCreateBookingRequest>({
      query: body => ({
        url: "/api/ams/bookings/staff",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AmsBookings"],
    }),
    /**
     * Each host's availability at a site over a window.
     *
     * `eventTypeIds` is the site's event types, not one of them, because that is where the site's
     * hours live and they are split across them — intake carries Monday to Thursday and off-slot
     * carries Friday. The server unions them into one entry per host; asking about one alone would
     * report the other's days as time nobody is working.
     *
     * `userIds` is required rather than optional: an empty filter asks the server about every host,
     * and a caller that meant "the four people on screen" would get a plausible-looking answer
     * covering everyone. Callers with no columns to ask about should skip the query. As with
     * my-schedule, the server refuses a span longer than three months.
     *
     * Tagged with the bookings rather than with an availability tag of its own, because Cal withholds
     * an hour it has already sold: an appointment's hour is missing from `dateRanges` exactly as an
     * hour off is. So every booking mutation changes this answer, and leaving it untagged left a
     * cancelled slot drawn as unavailable — the booking went on the refetch, and the hour it had
     * taken out of the host's ranges stayed out until something else refetched them. The grid reads
     * a row no window covers as one nobody is working; see `markUnavailable`.
     */
    getAvailability: builder.query<HostAvailability[], {
      teamId: number
      eventTypeIds: number[]
      userIds: number[]
      start: string
      end: string
    }>({
      query: ({ userIds, eventTypeIds, ...rest }) => ({
        url: "/api/ams/availability",
        params: { userIds: userIds.join(","), eventTypeIds: eventTypeIds.join(","), ...rest },
      }),
      keepUnusedDataFor: KEEP_FOR_SESSION,
      providesTags: ["AmsBookings"],
    }),
    /**
     * The signed-in staff member's own week: their sites, their bookings, their availability.
     *
     * One call for all three because they are useless apart — and because the server has to resolve
     * who the caller is in Cal before any of them can be asked. The window is required: this is a
     * calendar, and the server refuses a span longer than three months.
     */
    getMySchedule: builder.query<StaffSchedule, { start: string; end: string }>({
      query: (params) => ({ url: "/api/ams/my-schedule", params }),
      keepUnusedDataFor: KEEP_FOR_SESSION,
      providesTags: ["AmsBookings"],
    }),
    /**
     * One named staff member's week — the same payload as `getMySchedule`, about somebody else.
     *
     * A separate endpoint rather than a parameter on that one: this is what an administrator sees
     * when they open a single person's calendar from the team day, and it needs
     * `appointments:manage`. Whose week my-schedule answers about stays a question of the token
     * alone. `calUserId` is Cal's id — the id a team member carries, which is what the roster on
     * screen is made of.
     */
    getStaffSchedule: builder.query<StaffSchedule, { calUserId: number; start: string; end: string }>({
      query: ({ calUserId, ...params }) => ({ url: `/api/ams/staff-schedule/${calUserId}`, params }),
      keepUnusedDataFor: KEEP_FOR_SESSION,
      providesTags: ["AmsBookings"],
    }),
    /** One appointment from Cal, by uid — for opening the dialog from somewhere that is not a grid. */
    getBooking: builder.query<BookingDetails, string>({
      query: (bookingUid) => ({ url: `/api/ams/bookings/${encodeURIComponent(bookingUid)}` }),
      providesTags: ["AmsBookings"],
    }),
    /** Where one booking stands, and which statuses the signed-in person may move it to. */
    getBookingRequestStatus: builder.query<BookingStatusView, string>({
      query: (requestId) => ({ url: `/api/ams/booking-requests/${requestId}` }),
      providesTags: (_result, _error, requestId) => [{ type: "AmsBookingStatus" as const, id: requestId }, "AmsBookingStatus"],
    }),
    getBookingStatusHistory: builder.query<BookingStatusChange[], string>({
      query: (requestId) => ({ url: `/api/ams/booking-requests/${requestId}/history` }),
      providesTags: (_result, _error, requestId) => [{ type: "AmsBookingStatus" as const, id: requestId }, "AmsBookingStatus"],
    }),
    /** Bookings for the same household that this one could have been rescheduled to. Administrators only. */
    getRescheduleCandidates: builder.query<BookingRequestListItem[], string>({
      query: (requestId) => ({ url: `/api/ams/booking-requests/${requestId}/reschedule-candidates` }),
      providesTags: ["AmsBookingStatus"],
    }),
    /**
     * Appointments that are over and have no outcome yet, oldest first. Staff get the ones they host
     * whatever they ask for; administrators get everyone's, or one host's with `hostCalUserId`.
     */
    listBookingsNeedingUpdate: builder.query<NeedsUpdateResponse, {
      hostCalUserId?: number
      from?: string
      to?: string
      take?: number
      skip?: number
    }>({
      query: (params) => ({ url: "/api/ams/booking-requests/needs-update", params }),
      providesTags: ["AmsBookingStatus"],
    }),
    /**
     * Records what happened at an appointment, corrects an outcome, or marks a booking rescheduled.
     * Refusals come back as 400 (a reason is missing), 403 (not yours) or 409 (not possible now),
     * each carrying an `error` to show.
     *
     * Invalidates the grids as well: marking an upcoming booking rescheduled cancels it in Cal. And the
     * booking's notes: the server records the change as a StatusChange note.
     */
    setBookingStatus: builder.mutation<void, {
      requestId: string
      status: number
      reason?: string
      rescheduledToRequestId?: string
    }>({
      query: ({ requestId, ...body }) => ({
        url: `/api/ams/booking-requests/${requestId}/status`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        "AmsBookingStatus",
        "AmsBookings",
        { type: "AmsBookingNotes" as const, id: requestId },
      ],
    }),
  }),
})

export const {
  useGetTeamsQuery,
  useGetTeamMembersQuery,
  useGetTeamEventTypesQuery,
  useGetCalUsersQuery,
  useGetCmsUsersQuery,
  useGetBookingsQuery,
  useGetBookingFormDataQuery,
  useGetAvailabilityQuery,
  useGetMyScheduleQuery,
  useGetStaffScheduleQuery,
  useSetTeamMembershipMutation,
  useCancelBookingAsStaffMutation,
  useReassignBookingAsStaffMutation,
  useCreateBookingAsStaffMutation,
  useGetBookingQuery,
  useGetBookingRequestStatusQuery,
  useGetBookingStatusHistoryQuery,
  useGetRescheduleCandidatesQuery,
  useListBookingsNeedingUpdateQuery,
  useSetBookingStatusMutation,
} = bookingCalendarApi
